import React from 'react'
import { FilePdf } from 'phosphor-react'
import { Card, InboxGroups, ProfileStrip } from './Shared'
import {
    fmt,
    formatTime,
    formatDay,
    formatLongDate,
    scoreClass,
    findingsClass,
    toDate,
} from './format'

const STATE_PILL = {
    done: ['Done', 'ok'],
    now: ['Now', 'info'],
    late: ['Overdue', 'bad'],
}

const dueLabel = due => {
    const d = toDate(due)
    if (!d) return ''
    const today = new Date()
    return d.toDateString() === today.toDateString() ? formatTime(d) : formatDay(d)
}

// what a plan chip says after the customer: the result when done, else progress
const planResult = (p, caps) => {
    if (p.State === 'done') {
        if (caps.Scoring && p.Score !== null && p.Score !== undefined) return `${p.Score}%`
        return `${fmt(p.Findings)} findings`
    }
    return p.Progress ? `${fmt(p.Progress)}% done` : ''
}

const SUBMISSION_PILL = status => {
    const s = String(status || '').toLowerCase()
    if (s === 'returned') return 'warn'
    if (s === 'approved') return 'ok'
    return 'info'
}

// Score column when the company scores (no score = "–"), findings pill otherwise
const ResultCell = ({ row, scoring }) => {
    if (scoring) {
        if (row.Score === null || row.Score === undefined) return <span className="score">–</span>
        return <span className={`score ${scoreClass(row.Score)}`}>{row.Score}%</span>
    }
    return <span className={`pill ${findingsClass(row.Findings)}`}>{row.Findings ? fmt(row.Findings) : 'None'}</span>
}

export const AuditorView = ({ data, nav, canEdit, unit, unitTitle: Unit }) => {
    const caps = data.Capabilities || {}
    const my = data.My || {}
    const next = my.NextUp
    const plan = my.Plan || []
    const left = plan.filter(p => p.State !== 'done' && p.State !== 'late').length
    const late = plan.filter(p => p.State === 'late').length

    const weekDiff = (my.CompletedThisWeek || 0) - (my.CompletedLastWeek || 0)
    const kpis = [
        {
            l: 'Completed this week',
            v: fmt(my.CompletedThisWeek),
            x: weekDiff === 0 ? 'same as last week' : `${fmt(Math.abs(weekDiff))} ${weekDiff > 0 ? 'more' : 'fewer'} than last week`,
            c: weekDiff >= 0 ? 'ok' : 'warn',
        },
    ]
    if (caps.Scoring && my.Score !== null && my.Score !== undefined) {
 kpis.push({
            l: 'My average score',
            v: my.Score,
            s: '%',
            x: my.TeamScore !== null && my.TeamScore !== undefined ? `team ${my.TeamScore}%` : '',
            c: scoreClass(my.Score),
        })
}
    kpis.push({
        l: 'Findings I raised',
        v: fmt(my.FindingsRaised),
        x: `${fmt(my.FindingsOpen)} still open`,
        c: my.FindingsOpen ? 'warn' : 'ok',
    })
    if (caps.Tickets) {
 kpis.push({
            l: 'Tickets assigned to me',
            v: fmt(my.TicketsAssigned),
            x: `${fmt(my.TicketsPastDue)} past due · ${fmt(my.TicketsUnacknowledged)} not acknowledged`,
            c: my.TicketsPastDue ? 'bad' : '',
        })
}
    kpis.push({
        l: 'Drafts to finish',
        v: fmt(my.Drafts),
        x: my.OldestDraftOn ? `oldest ${formatDay(my.OldestDraftOn)}` : '',
    })

    const actions = (data.Inbox || []).filter(g => g && g.Count > 0)

    return (
        <>
            <section className="next">
                {next ? (
                    <div>
                        <div className="eyebrow">
                            Next up{next.Due ? ` · ${next.State === 'late' ? 'was due ' : ''}${dueLabel(next.Due)}` : ''}
                        </div>
                        <h1>{next.Template || next.Title}</h1>
                        <div className="sub">{[next.Customer, next.Title !== next.Template ? next.Title : ''].filter(Boolean).join(' · ')}</div>
                        <div className="bar">
                            <i style={{ width: `${Math.max(0, Math.min(next.Progress || 0, 100))}%` }} />
                        </div>
                        <div className="sub" style={{ marginTop: 6 }}>
                            {fmt(next.Progress)}% done · {fmt(left)} {unit(left)} left today
                            {late ? `, ${fmt(late)} overdue` : ''}
                        </div>
                    </div>
                ) : (
                    <div>
                        <div className="eyebrow">{formatLongDate()}</div>
                        <h1>Nothing planned for you</h1>
                        <div className="sub">No open tasks are assigned to you. Start a new {unit(1)} from a template.</div>
                    </div>
                )}
                <div className="acts">
                    {next && (
                        <button type="button" className="hd-btn w" onClick={() => nav.openAudit(next)}>
                            {next.Progress > 0 ? 'Resume' : 'Open'}
                        </button>
                    )}
                    <button type="button" className={`hd-btn ${next ? 'g' : 'w'}`} onClick={nav.startNew}>
                        New {unit(1)}
                    </button>
                </div>
            </section>
            {canEdit && <ProfileStrip data={data} unit={unit} canEdit={canEdit} onSettings={nav.openSettings} />}

            <div className="dayhead">
                <h3>My day · {formatLongDate()}</h3>
                <button type="button" className="hd-link" onClick={nav.openTasks}>
                    All my tasks
                </button>
            </div>
            {plan.length ? (
                <div className="strip">
                    {plan.map(p => {
                        const pill = STATE_PILL[p.State]
                        return (
                            <button
                                type="button"
                                key={p.TaskId}
                                className={`chip ${p.State || ''}`}
                                style={p.TemplateColor ? { '--chip-color': p.TemplateColor } : undefined}
                                onClick={() => (p.State === 'done' ? nav.openReport(p) : nav.openAudit(p))}
                            >
                                <time>{dueLabel(p.Due)}</time>
                                <span className="t">{p.Template || p.Title}</span>
                                <span className="s">
                                    {[p.Customer, planResult(p, caps)].filter(Boolean).join(' · ')}
                                </span>
                                {pill && (
                                    <span className="st">
                                        <span className={`pill ${pill[1]}`}>{pill[0]}</span>
                                    </span>
                                )}
                            </button>
                        )
                    })}
                </div>
            ) : (
                <div className="empty">No tasks due today.</div>
            )}

            <div className="kstrip" style={{ '--k': kpis.length }}>
                {kpis.map(k => (
                    <div key={k.l}>
                        <span className="l">{k.l}</span>
                        <span className="v">
                            {k.v}
                            {k.s && <small>{k.s}</small>}
                        </span>
                        {k.x && <span className={`x ${k.c || ''}`}>{k.x}</span>}
                    </div>
                ))}
            </div>

            <div className="body halves">
                <div className="card">
                    <div className="card-h">
                        <div>
                            <h3>Needs my action</h3>
                            <div className="sub">
                                Overdue {unit(2)}, {caps.Tickets ? 'tickets assigned to me, ' : ''}unfinished drafts
                            </div>
                        </div>
                    </div>
                    <div className="inbox">
                        {actions.length ? <InboxGroups groups={actions} nav={nav} /> : <div className="card-b empty">All clear.</div>}
                    </div>
                </div>
                <Card title="My recent submissions" sub="What happened to the reports you sent">
                    {!my.RecentSubmissions?.length ? (
                        <div className="empty">You have not completed any {unit(2)} in this period.</div>
                    ) : (
                        <table className="tbl">
                            <thead>
                                <tr>
                                    <th>{Unit(1)}</th>
                                    <th className="num">{caps.Scoring ? 'Score' : 'Findings'}</th>
                                    <th>Status</th>
                                    <th className="num">PDF</th>
                                </tr>
                            </thead>
                            <tbody>
                                {my.RecentSubmissions.map(r => (
                                    <tr key={r.AuditId}>
                                        <td>
                                            <div style={{ fontWeight: 600 }}>{r.Name}</div>
                                            <div className="sub">{[r.Customer, formatDay(r.CompletedOn)].filter(Boolean).join(' · ')}</div>
                                        </td>
                                        <td className="num">
                                            <ResultCell row={r} scoring={caps.Scoring} />
                                        </td>
                                        <td>
                                            <span className={`pill ${SUBMISSION_PILL(r.Status)}`}>{r.Status}</span>
                                        </td>
                                        <td className="num">
                                            {r.HasPdf && (
                                                <button type="button" className="hd-btn sm o" title="Open PDF" onClick={() => nav.openReport(r)}>
                                                    <FilePdf size={14} />
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </Card>
            </div>
        </>
    )
}
